import React from 'react';
import { Box } from '@mui/material';
import CountrySelector from '../../common/CountrySelector';
import EmployeeContractTypeSelector from '../../common/EmployeeContractTypeSelector';
import ClusterSelector from '../../common/ClusterSelector';
import CompanySelector from '../../common/CompanySelector';
import PositionSelector from '../../common/PositionSelector';
import GradeSelector from '../../common/GradeSelector';
import { importApi } from '../../../services/api';
import { createBaseImportConfig, FieldTypes, CommonFields } from './baseImportConfig';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../constants/orgStructureTypes';
import { 
  COUNTRY_RETURN_TYPES, 
  CONTRACT_TYPE_RETURN_TYPES, 
  CLUSTER_RETURN_TYPES, 
  COMPANY_RETURN_TYPES, 
  POSITION_RETURN_TYPES, 
  GRADE_RETURN_TYPES 
} from '../../../constants/selectorReturnTypes';

const employeeConfig = {
  entityName: 'Individual',
  entityNamePlural: 'Individuals',
  
  requiredFields: [
    'firstName', 
    'lastName', 
    'hireDate', 
    'contractType', 
    'deliveryCenter', 
    'workingLocation', 
    'contractCompany', 
    'position'
  ],
  optionalFields: ['grade'],
  
  // Field configurations
  fields: {
    firstName: { 
      type: FieldTypes.TEXT, 
      label: 'First Name',
      required: true
    },
    lastName: { 
      type: FieldTypes.TEXT, 
      label: 'Last Name',
      required: true
    },
    hireDate: { 
      type: FieldTypes.TEXT, 
      label: 'Hire Date',
      required: true
    },
    contractType: { 
      type: FieldTypes.SELECTOR, 
      label: 'Contract Type',
      component: EmployeeContractTypeSelector,
      returnType: CONTRACT_TYPE_RETURN_TYPES.NAME,
      required: true
    },
    // ADR-119 amendment 1: an individual is hired into a Region. The CSV
    // column header stays `deliveryCenter` so existing import files keep
    // working; only the label and the offered values change.
    deliveryCenter: { 
      type: FieldTypes.SELECTOR, 
      label: 'Region',
      component: ClusterSelector,
      returnType: CLUSTER_RETURN_TYPES.NAME,
      props: {
        structureTypeCodes: [REGION_STRUCTURE_TYPE_CODE],
        outOfScopeSuffix: 'not a Region'
      },
      required: true
    },
    workingLocation: { 
      type: FieldTypes.SELECTOR, 
      label: 'Working Location',
      component: CountrySelector,
      returnType: COUNTRY_RETURN_TYPES.NAME,
      required: true
    },
    contractCompany: { 
      type: FieldTypes.SELECTOR, 
      label: 'Contract Company',
      component: CompanySelector,
      returnType: COMPANY_RETURN_TYPES.NAME,
      dependsOn: 'deliveryCenter',
      dependencyPropName: 'clusterName', // CompanySelector expects clusterName prop
      required: true
    },
    position: { 
      type: FieldTypes.SELECTOR, 
      label: 'Position',
      component: PositionSelector,
      returnType: POSITION_RETURN_TYPES.NAME,
      required: true,
      onPositionChange: (positionName, positionData, context) => {
        if (positionData) {
          const requiresGrade = positionData.requiresGrade || false;
          
          // Update the position requirements in custom state
          if (context.setCustomState) {
            context.setCustomState(state => ({
              ...state,
              positionRequiresGrade: {
                ...state.positionRequiresGrade,
                [positionName]: requiresGrade
              }
            }));
          }
          
          // Also update the item's positionRequiresGrade property to override API data
          context.updateField('positionRequiresGrade', requiresGrade);
          
          // If position doesn't require grade, always clear the grade field
          if (!requiresGrade) {
            context.updateField('grade', '');
          }
        } else if (positionName) {
          // If we have a position name but no position data, assume it doesn't require grade
          // This handles cases where the position is typed in but not selected from dropdown
          if (context.setCustomState) {
            context.setCustomState(state => ({
              ...state,
              positionRequiresGrade: {
                ...state.positionRequiresGrade,
                [positionName]: false
              }
            }));
          }
          context.updateField('positionRequiresGrade', false);
          context.updateField('grade', '');
        }
      },
      onSelectedPositionChange: (positionData, context) => {
        if (positionData) {
          const requiresGrade = positionData.requiresGrade || false;
          const currentPosition = context.getFieldValue('position');
          
          if (currentPosition && context.setCustomState) {
            // Update the position requirements cache
            context.setCustomState(state => ({
              ...state,
              positionRequiresGrade: {
                ...state.positionRequiresGrade,
                [currentPosition]: requiresGrade
              }
            }));
          }
        }
      }
    },
    grade: { 
      type: FieldTypes.SELECTOR, 
      label: 'Grade',
      component: GradeSelector,
      returnType: GRADE_RETURN_TYPES.NAME,
      conditional: (employee, context) => {
        const requiresGrade = context.doesPositionRequireGrade?.(employee) || false;
        return requiresGrade;
      },
      required: (employee, context) => context.doesPositionRequireGrade?.(employee) || false,
      // Custom render when grade field is hidden
      renderWhenHidden: (employee) => {
        const hasPosition = !!employee.position;
        
        if (!hasPosition) {
          return (
            <Box sx={{ 
              color: '#D1D5DB',
              fontStyle: 'italic',
              fontSize: '12px',
              cursor: 'not-allowed',
              userSelect: 'none',
              padding: '8px'
            }}>
              Select position first
            </Box>
          );
        }
        
        // Position exists but doesn't require grade - show N/A
        return (
          <Box sx={{ 
            color: '#9CA3AF',
            fontStyle: 'italic',
            fontSize: '12px',
            cursor: 'not-allowed',
            userSelect: 'none',
            padding: '8px'
          }}>
            N/A
          </Box>
        );
      },
      hiddenText: 'N/A'
    }
  },
  
  // Table column order
  tableColumns: [
    { field: 'status', label: 'Status', width: 120 },
    //{ field: 'userStatus', label: 'User Status', width: 200 },
    { field: 'firstName', label: 'First Name', width: 150 },
    { field: 'lastName', label: 'Last Name', width: 150 },
    { field: 'hireDate', label: 'Hire Date', width: 120 },
    { field: 'contractType', label: 'Contract Type', width: 150 },
    { field: 'deliveryCenter', label: 'Region', width: 180 },
    { field: 'contractCompany', label: 'Contract Company', width: 180 },
    { field: 'workingLocation', label: 'Working Location', width: 180 },
    { field: 'position', label: 'Position', width: 150 },
    { field: 'grade', label: 'Grade', width: 100 },
    { field: 'actions', label: 'Actions', width: 80 }
  ],
  
  // API endpoints
  api: {
    validate: (rows) => importApi.validateEmployees(rows),
    import: (rows) => importApi.importEmployees(rows)
  },
  
  // Status configurations
  statusConfig: {
    userStatuses: {
      'checking': { 
        label: 'Checking...', 
        color: '#6366F1',
        tooltip: 'Validation in progress'
      },
      'ok_to_import': { 
        label: 'Ready to Import', 
        color: '#10B981',
        tooltip: 'Found in Azure AD and not already in the system'
      },
      'already_employee': {
        label: 'Already Individual',
        color: '#F59E0B',
        tooltip: 'Already in the system'
      },
      'not_in_azure': {
        label: 'Not in Azure AD',
        color: '#EF4444',
        tooltip: 'Individual not found in Azure AD'
      },
      'already_employee_not_in_azure': {
        label: 'Already Individual, Not in Azure AD',
        color: '#8B5CF6',
        tooltip: 'Individual exists in the system but not in Azure AD'
      }
    },
    validationStatuses: {
      'valid': { label: 'Valid', color: '#10B981' },
      'invalid': { label: 'Invalid', color: '#EF4444' },
      'pending': { label: 'Pending', color: '#6366F1' }
    },
    importStatuses: {
      'success': { label: 'Imported', color: '#059669', backgroundColor: '#ECFDF5' },
      'failed': { label: 'Import Failed', color: '#DC2626', backgroundColor: '#FEF2F2' }
    }
  },
  
  // Documentation
  documentation: {
    description: 'Upload a CSV file with individual data to import into the system',
    fileFormat: {
      dateFormat: 'YYYY-MM-DD',
      notes: [
        'Contract Company is dependent on Region',
        'The CSV column header for the Region stays `deliveryCenter`',
        'Grade is only required for Positions that are configured to have a grade'
      ]
    }
  },
  
  // Fields that shouldn't be marked as pending when changed
  skipPendingFields: ['grade', 'positionRequiresGrade'],
  
  // Hooks for employee-specific logic
  hooks: {
    // Initialize position requirements state
    initializeCustomState: ({ setCustomState }) => {
      setCustomState({ positionRequiresGrade: {} });
    },
    
    // Handle post-validation logic
    onPostValidation: (validatedData, { setCustomState }) => {
      // Extract position requirements from validation response
      const positionRequirementsMap = {};
      validatedData.forEach(item => {
        if (item.position && item.validationStatus === 'valid' && item.positionRequiresGrade !== undefined) {
          positionRequirementsMap[item.position] = item.positionRequiresGrade;
        }
      });
      
      // Update position requirements in custom state
      if (Object.keys(positionRequirementsMap).length > 0) {
        setCustomState(state => ({
          ...state,
          positionRequiresGrade: {
            ...state.positionRequiresGrade,
            ...positionRequirementsMap
          }
        }));
      }
    },
    
    // Handle field changes
    onFieldChange: (itemId, field, value, { updateItem, customState }) => {
      // If position changed and new position doesn't require grade, clear the grade
      if (field === 'position' && customState.positionRequiresGrade?.[value] === false) {
        updateItem(itemId, { grade: '' });
      }
    },
    
    // Extend field context with position requirements logic
    getFieldContext: (customState) => ({
      positionRequiresGrade: customState.positionRequiresGrade || {},
      setPositionRequiresGrade: () => {
        // This is handled in onPostValidation now
      },
      doesPositionRequireGrade: (item) => {
        const positionRequiresGrade = customState.positionRequiresGrade || {};
        // First check if the row itself has positionRequiresGrade from validation response
        if (item.positionRequiresGrade !== undefined) {
          return item.positionRequiresGrade;
        }
        
        // Then check the cache for user-edited positions
        if (item.position in positionRequiresGrade) {
          return positionRequiresGrade[item.position];
        }
        
        // Default to false (don't show grade field) until we know the position requires it
        return false;
      }
    })
  },
  
  // Import logic helpers
  helpers: {
    getImportableItems: (employees) => {
      return employees.filter(emp => emp.userStatus === 'ok_to_import' && emp.validationStatus === 'valid');
    },
    
    // Backwards compatibility
    getImportableEmployees: function(employees) {
      return this.getImportableItems(employees);
    },
    
    getStatusCounts: (employees) => {
      return {
        valid: employees.filter(emp => emp.validationStatus === 'valid').length,
        invalid: employees.filter(emp => emp.validationStatus === 'invalid').length,
        importable: employees.filter(emp => emp.userStatus === 'ok_to_import' && emp.validationStatus === 'valid').length,
        alreadyEmployee: employees.filter(emp => emp.userStatus === 'already_employee').length,
        notInAzure: employees.filter(emp => emp.userStatus === 'not_in_azure').length,
        inconsistentData: employees.filter(emp => emp.userStatus === 'already_employee_not_in_azure').length
      };
    },
    
    // Backwards compatibility
    getEmployeeStatusCounts: function(employees) {
      return this.getStatusCounts(employees);
    },
    
    // Custom processing for employee validation response
    processValidationResponse: (response) => {
      return response.rows.map((row, index) => ({
        ...row.data,
        id: `temp_${Date.now()}_${index}`,
        validationStatus: row.validationStatus,
        validationErrors: row.validationErrors || [],
        userStatus: row.userStatus,
        adDetails: row.adDetails,
        editable: row.editable,
        rowIndex: row.rowIndex,
        ...(row.positionRequiresGrade !== undefined && { positionRequiresGrade: row.positionRequiresGrade })
      }));
    },
    
    // Custom import tooltip messages for employees
    getImportTooltip: (stats, config) => {
      // Check for employee-specific status messages
      if (stats.alreadyEmployee > 0) {
        return `All ${config.entityNamePlural.toLowerCase()} already exist in the system`;
      } else if (stats.notInAzure > 0 || stats.inconsistentData > 0) {
        return `No ${config.entityNamePlural.toLowerCase()} ready to import - check Azure AD status`;
      }
      // Fall back to default
      return `No ${config.entityNamePlural.toLowerCase()} ready to import`;
    }
  }
};

export const employeeImportConfig = createBaseImportConfig(employeeConfig); 